import api from '../config/api';
import {
  CheckoutPlan,
  Payment,
  PaymentSettings,
  PaymentListFilters,
  CreatePaymentRequest,
  UpdatePaymentStatusRequest,
  GeneratePaymentsRequest,
  GeneratePaymentsResult,
} from '../types/payment';

export interface XpayCheckoutResponse {
  paymentId: string;
  orderId: string;
  hostedPageUrl: string;
}

export interface XpayCheckoutResult {
  paymentId: string;
  status: string;
  isPaid: boolean;
  message?: string;
  payment?: Payment;
  bundledYears?: number[];
}

export const paymentService = {
  getMyPayments: async (): Promise<Payment[]> => {
    const response = await api.get<Payment[]>('/payments');
    return response.data;
  },

  getPayment: async (id: string): Promise<Payment> => {
    const response = await api.get<Payment>(`/payments/${id}`);
    return response.data;
  },

  getCheckoutPlan: async (paymentId: string): Promise<CheckoutPlan> => {
    const response = await api.get<CheckoutPlan>('/payments/checkout-plan', {
      params: { paymentId },
    });
    return response.data;
  },

  startCheckout: async (paymentId: string): Promise<XpayCheckoutResponse> => {
    const response = await api.post<XpayCheckoutResponse>(`/payments/${paymentId}/checkout`);
    return response.data;
  },

  getCheckoutResult: async (paymentId: string): Promise<XpayCheckoutResult> => {
    const response = await api.get<XpayCheckoutResult>(`/payments/${paymentId}/result`);
    return response.data;
  },

  downloadReceipt: async (paymentId: string): Promise<Blob> => {
    const response = await api.get(`/payments/${paymentId}/receipt`, {
      responseType: 'blob',
    });
    return response.data;
  },

  getAllPayments: async (filters?: PaymentListFilters): Promise<Payment[]> => {
    const params = buildPaymentFilterParams(filters);
    const response = await api.get<Payment[]>(`/admin/payments?${params.toString()}`);
    return response.data;
  },

  exportPayments: async (filters?: PaymentListFilters): Promise<{ blob: Blob; fileName: string }> => {
    const params = buildPaymentFilterParams(filters);
    const response = await api.get(`/admin/payments/export?${params.toString()}`, {
      responseType: 'blob',
    });
    const disposition = response.headers['content-disposition'] as string | undefined;
    const fileName = parseFileNameFromDisposition(disposition) ?? 'ICOM_Payments.xlsx';
    return { blob: response.data, fileName };
  },

  createPayment: async (request: CreatePaymentRequest): Promise<Payment> => {
    const response = await api.post<Payment>('/admin/payments', request);
    return response.data;
  },

  updatePaymentStatus: async (
    paymentId: string,
    request: UpdatePaymentStatusRequest
  ): Promise<Payment> => {
    const response = await api.put<Payment>(`/admin/payments/${paymentId}/status`, request);
    return response.data;
  },

  generateReceipt: async (paymentId: string): Promise<Blob> => {
    const response = await api.post(
      `/admin/payments/${paymentId}/generate-receipt`,
      {},
      { responseType: 'blob' }
    );
    return response.data;
  },

  getPaymentSettings: async (year: number): Promise<PaymentSettings> => {
    const response = await api.get<PaymentSettings>(`/admin/payments/settings?year=${year}`);
    return response.data;
  },

  updatePaymentSettings: async (settings: PaymentSettings): Promise<PaymentSettings> => {
    const response = await api.post<PaymentSettings>('/admin/payments/settings', settings);
    return response.data;
  },

  generateYearlyPayments: async (
    request: GeneratePaymentsRequest
  ): Promise<GeneratePaymentsResult> => {
    const response = await api.post<GeneratePaymentsResult>('/admin/payments/generate', request);
    return response.data;
  },
};

function buildPaymentFilterParams(filters?: PaymentListFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (!filters) return params;
  if (filters.year != null) params.append('year', filters.year.toString());
  if (filters.status != null) params.append('status', filters.status.toString());
  if (filters.memberId) params.append('memberId', filters.memberId);
  if (filters.membershipType != null) {
    params.append('membershipType', filters.membershipType.toString());
  }
  if (filters.paidFrom) params.append('paidFrom', filters.paidFrom);
  if (filters.paidTo) params.append('paidTo', filters.paidTo);
  if (filters.search?.trim()) params.append('search', filters.search.trim());
  if (filters.lang) params.append('lang', filters.lang);
  return params;
}

function parseFileNameFromDisposition(disposition?: string): string | null {
  if (!disposition) return null;
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim());
    } catch {
      return utf8Match[1].trim();
    }
  }
  const plainMatch = /filename="?([^";]+)"?/i.exec(disposition);
  return plainMatch?.[1]?.trim() ?? null;
}
